/**
 * @file Layers.repository.ts
 * @module LayersRepository
 * @description Reaktīvā stāvokļa krātuve un TypeScript saskarnes kartes slāņu datiem.
 *              Definē datu modeli slāņu grupām, atsevišķiem slāņiem, stiliem
 *              (punktu/līniju) un slāņu kopu konfigurācijām. Izmanto @ngneat/elf
 *              reaktīvai stāvokļa pārvaldībai — patērē slāņu pārslēdzējs un stilu ģenerators.
 *              Dati nāk no Kartes vizualizācijas pārvaldības konfigurācijas.
 */

import { createStore } from "@ngneat/elf";
import { withEntities, selectAllEntities, setEntities } from "@ngneat/elf-entities";

/** Mapbox/MapTiler style parameters for MVT base layers */
export interface ParametersParameters {
	version?: number | string;
	name?: string;
	metadata?: any;
	sources?: any;
	glyphs?: string;
	layers?: any[];
	id?: string;
	sprite?: string;
	srs?: string;
	style?: string;
}

/** Backend API response for a remote/data layer group (category) */
export interface RemoteResponse {
	/** Layer group ID from backend */
	id: number;
	/** Display name of the layer group */
	name: string;
	/** Foreign key to LayerCategoryBase model in Django */
	layercategorybase_ptr_id: number;
	/** Individual layers within this group */
	layers: LayerResponse[];
	/** Attribution text (data source credits) */
	attributions: string;
	/** URL to the open data catalog entry */
	url_to_datasource: string;
	/** Whether to show UVIS-specific popups for this group's layers */
	use_uvis_popups: boolean;
}

/** Backend API response for a layer set (preconfigured layer combination) */
export interface LayerSetResponse {
	/** Layer set ID */
	id: number;
	/** Display name */
	name: string;
	/** Whether the layer set is published and available to users */
	published: boolean;
	/** Array of data layer IDs included in this set */
	data_layers: number[];
	/** Whether this set is enabled by default */
	enabled: boolean;
	/** SVG icon for the layer set button */
	svg_symbol: string;
}

/** WMS layer request parameters */
export interface LayerParameters {
	/** WMS LAYER parameter value */
	LAYER: string;
	/** WMS VERSION (e.g., "1.1.1") */
	VERSION: string;
	/** WMS SERVICE (always "WMS") */
	SERVICE: string;
}

/** Backend API response for a single layer (base or remote) */
export interface LayerResponse {
	/** Layer ID */
	id: number;
	/** Display name */
	name: string;
	/** Opacity percentage (0-100, where 0 = fully opaque, 100 = fully transparent) */
	opacity: number;
	/** Whether the layer is enabled (shown in layer switcher) */
	enabled: boolean;
	/** Whether the layer is currently visible on the map */
	visible: boolean;
	/** Data source attribution text */
	attributions: string;
	/** Layer type: "mvt" (Mapbox vector tiles), "xyz" (raster tiles), "wms" */
	type: string;
	/** Tile URL template */
	url: string;
	/** Layer-type-specific parameters (Mapbox style object, WMS params, etc.) */
	parameters: any;
	/** URL to layer thumbnail image (for base layer switcher) */
	thumbnail: string | null;
	/** Foreign key to RemoteSourcesBase model */
	remotesourcesbase_ptr_id: number;
}

/** Backend API response for a data layer group with vector styles */
export interface DataResponse {
	/** Group ID */
	id: number;
	/** Display name */
	name: string;
	/** Foreign key to LayerCategoryBase */
	layercategorybase_ptr_id: number;
	/** Attribution text */
	attributions: string;
	/** URL to open data catalog */
	url_to_datasource: string;
	/** Styled layers within this data group */
	layers: LayerStyle[];
}

/**
 * Complete style definition for a vector data layer.
 * Combines backend metadata with point/line style rules for rendering.
 */
export interface LayerStyle {
	/** MVT tile URL template (pg_tileserv endpoint) */
	mvt_url: string;
	/** Layer ID */
	id: number;
	/** Parent category ID */
	category_id: number;
	/** Optional API filter for data queries */
	data_api_filter: string | null;
	/** Display name */
	name: string;
	/** Parent group name (set during processing) */
	groupName: string;
	/** PostgreSQL table name (e.g., "kafkamessages_notplannedevent") */
	table: string;
	/** Merged tile URL if multiple tables share a tile source */
	merged_tiles: string | null;
	/** Original table name before merge renaming */
	original_table?: string;
	/** Whether shown in layer switcher by default */
	enabled: boolean;
	/** Current visibility state */
	visible: boolean;
	/** Feature property name used for labels */
	label: string;
	/** Label rendering style (e.g., "road_shield", "pikets_box") */
	label_style: string;
	/** Comma-separated feature property names shown in popups */
	attributes: string;
	/** Attribution text */
	attributions: string;
	/** Minimum zoom level to display this layer */
	zoom_from: number;
	/** Maximum zoom level to display this layer */
	zoom_to: number;
	/** Opacity percentage (0-100) */
	opacity: number;
	/** Point style rules (icon, color, filter conditions) */
	points: PointStyle[];
	/** Line style rules (stroke, color, dash patterns) */
	lines: LineStyle[];
	/** Whether features should be clustered at lower zoom levels */
	clustered: boolean;
	/** Sort index within the group */
	index: number;
	/** Whether to use UVIS-specific popup rendering */
	use_uvis_popups?: boolean;
	/** Whether any records exist for this layer (used for visibility optimization) */
	records_exist: boolean;
}

/** Point/icon style definition for vector features (from QGIS style configuration) */
export interface PointStyle {
	/** Style rule ID */
	id: number;
	/** Parent layer ID */
	layer_id: number;
	/** Style rule display name */
	name: string;
	/** Label rendering style (e.g., "pikets_box", "declutter_off") */
	label_style: string | null;
	/** Parent layer name */
	layerName: string;
	/** Parent group name */
	groupName: string;
	/** QGIS filter expression (e.g., "status is 'active' and type is 'camera'") */
	filter: string;
	/** Icon image URL (SVG pictogram, e.g., "/gfiles/visualization/.../c_camera.svg") */
	piktogram: string;
	/** Fill color (hex, e.g., "#FF0000") */
	color: string;
	/** Icon/circle size in pixels */
	size: number;
	/** Icon rotation in degrees */
	rotation: number;
	/** Icon horizontal offset in pixels */
	offset_x: number;
	/** Icon vertical offset in pixels */
	offset_y: number;
	/** Minimum zoom level for this style rule */
	minZoom: number;
	/** Maximum zoom level for this style rule */
	maxZoom: number;
	/** Opacity (0-1 scale) */
	opacity: number;
	/** MVT source-layer name override */
	source?: string;
	/** Custom popup title */
	popup_title?: string;
	/** Whether to include in cluster layer */
	clustered: boolean;
}

/** Line/stroke style definition for vector features (from QGIS style configuration) */
export interface LineStyle {
	/** Style rule ID */
	id: number;
	/** Parent layer ID */
	layer_id: number;
	/** Style rule display name */
	name: string;
	/** Parent layer name */
	layerName: string;
	/** Parent group name */
	groupName: string;
	/** QGIS filter expression (null = match all line features) */
	filter: string | null;
	/** Stroke width in pixels */
	width: number;
	/** Line dash offset */
	offset: number | null;
	/** Stroke style type: "solid" (default), "contour", "dash", "dot", "dash_contour" */
	stroke_style: string;
	/** Stroke color (hex) */
	color: string;
	/** Line join style: "round", "bevel", "miter" */
	join_style: string;
	/** Line cap style: "round", "butt", "square", "line" (line = show end caps) */
	cap_style: string;
	/** Icon URL for symbols placed along the line */
	piktogram: string;
	/** Minimum zoom level */
	minZoom: number;
	/** Maximum zoom level */
	maxZoom: number;
	/** Opacity (0-1 scale) */
	opacity: number;
	/** MVT source-layer name override */
	source?: string;
	/** Label rendering style (e.g., "road_shield") */
	label_style: string;
	/** Feature property name for label text */
	label: string;
}

/** Layer entity with group classification (extends base LayerResponse) */
export interface LayerEntity extends LayerResponse {
	/** Layer category: "base" (base maps), "data" (vector data), "remote" (external WMS/tiles) */
	group: "base" | "data" | "remote";
}

/** Alternative layer entity for styled data layers (extends LayerStyle) */
export interface AltLayerEntity extends LayerStyle {
	/** Layer category */
	group: "base" | "data" | "remote";
}

/** A group of related layers (corresponds to a layer category in the backend) */
export interface GroupEntity {
	/** Attribution text for the group */
	attributions?: string;
	/** Unique group ID (format: "{group}-{backendId}", e.g., "data-5") */
	id: string;
	/** Display name */
	name: string;
	/** Group category */
	group: "base" | "data" | "remote";
	/** Layers within this group */
	layers: LayerEntity[] | AltLayerEntity[];
	/** URL to open data catalog entry */
	dataSource?: string;
	/** Whether UVIS popup rendering is enabled */
	use_uvis_popups: boolean;
	/** Thumbnail URL for the group */
	thumbnail?: string;
}

const STORE = createStore({ name: "streets" }, withEntities<GroupEntity>());

/**
 * Returns an observable of all layer group entities.
 * @returns {Observable<GroupEntity[]>} Observable emitting the full list of layer groups
 */
export const getGroupEntities = () => {
	return STORE.pipe(selectAllEntities());
};

/**
 * Replaces all layer group entities in the store.
 * Called after fetching layer configuration from the backend API.
 *
 * @param {GroupEntity[]} results - Complete list of layer groups to store
 */
export const setLayerEntities = (results: GroupEntity[]) => {
	STORE.update(setEntities(results));
};
